import { type ProductFormValues } from "@/types/product-type";
import { sanitizeString } from "@/utils/string-util";

export function normalizeCreateValues(
  values: ProductFormValues
): Omit<ProductFormValues, "colorValues" | "sizeValues"> {
  // Transform colorValues and sizeValues into attributes array
  const attributes: Array<{ name: string; values: string[] }> = [];

  if (values.colorValues && values.colorValues.length > 0) {
    attributes.push({
      name: "Color",
      values: values.colorValues,
    });
  }

  if (values.sizeValues && values.sizeValues.length > 0) {
    attributes.push({
      name: "Size",
      values: values.sizeValues,
    });
  }

  // If attributes already exist, merge them
  if (values.attributes && values.attributes.length > 0) {
    values.attributes.forEach((attr) => {
      // Only add if not already added from colorValues/sizeValues
      const existingIndex = attributes.findIndex((a) => a.name === attr.name);
      if (existingIndex >= 0) {
        // Merge values if attribute already exists
        const existingValues = attributes[existingIndex].values;
        const mergedValues = [...new Set([...existingValues, ...attr.values])];
        attributes[existingIndex].values = mergedValues;
      } else {
        attributes.push(attr);
      }
    });
  }

  // Remove temporary fields and return only DTO fields
  const { colorValues, sizeValues, ...restValues } = values;

  const cleanCategoryId =
    restValues.categoryId && restValues.categoryId !== "none" && restValues.categoryId.trim() !== ""
      ? restValues.categoryId.trim()
      : null;

  return {
    code: restValues.code,
    name: restValues.name,
    description: sanitizeString(restValues.description),
    unit: sanitizeString(restValues.unit),
    price: restValues.price,
    cost: restValues.cost,
    imageUrl: sanitizeString(restValues.imageUrl),
    isActive: restValues.isActive ?? false,
    onStoreFront: restValues.onStoreFront ?? false,
    categoryId: cleanCategoryId,
    attributes: attributes.length > 0 ? attributes : undefined,
    initStock: restValues.initStock ?? 0,
  };
}

export function normalizeUpdateValues(values: ProductFormValues): {
  code?: string;
  name?: string;
  description?: string;
  unit?: string;
  price?: number;
  cost?: number;
  imageUrl?: string;
  isActive?: boolean;
  onStoreFront?: boolean;
  categoryId?: string | null;
} {
  // Remove temporary fields and return only DTO fields
  // ProductUpdateRequest does NOT include attributes field
  const { colorValues, sizeValues, attributes, ...restValues } = values;

  const cleanCategoryId =
    restValues.categoryId && restValues.categoryId !== "none" && restValues.categoryId.trim() !== ""
      ? restValues.categoryId.trim()
      : null;

  // Only include fields that are present in the DTO
  // All fields are optional in ProductUpdateRequest
  return {
    code: restValues.code,
    name: restValues.name,
    description: sanitizeString(restValues.description),
    unit: sanitizeString(restValues.unit),
    price: restValues.price,
    cost: restValues.cost,
    imageUrl: sanitizeString(restValues.imageUrl),
    isActive: restValues.isActive,
    onStoreFront: restValues.onStoreFront,
    categoryId: cleanCategoryId,
  };
}
